"use client"

/**
 * Google Analytics 4 — Consent-gated.
 *
 * gtag.js wird NUR geladen, wenn (a) GA in den Backend-Settings aktiviert ist
 * (analytics.gaEnabled + Mess-ID) und (b) der Besucher im Cookie-Banner
 * eingewilligt hat. Ohne Einwilligung wird kein einziges Byte an Google
 * gesendet (strenger als Consent Mode — DSGVO-sicher).
 *
 * Events aus Fremd-Komponenten (z.B. packages/ui-Buchungsstrecke) kommen über
 * das CustomEvent `mk:track` herein — so braucht das Paket keine Abhängigkeit
 * zu dieser Datei und funktioniert auch ohne Analytics.
 */

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

const CONSENT_KEY = "mk-cookie-consent" // "granted" | "denied"

export type ConsentValue = "granted" | "denied" | null

export function getStoredConsent(): ConsentValue {
  if (typeof window === "undefined") return null
  try {
    const v = localStorage.getItem(CONSENT_KEY)
    return v === "granted" || v === "denied" ? v : null
  } catch {
    return null
  }
}

export function storeConsent(value: "granted" | "denied"): void {
  try {
    localStorage.setItem(CONSENT_KEY, value)
  } catch {
    /* Private Mode o.ä. — Banner erscheint dann erneut */
  }
  window.dispatchEvent(new CustomEvent("mk:consent:changed", { detail: value }))
}

/** Banner erneut öffnen (Footer-Link „Cookie-Einstellungen"). */
export function reopenConsentBanner(): void {
  window.dispatchEvent(new CustomEvent("mk:consent:open"))
}

let gaLoaded = false

/** gtag.js einbinden und konfigurieren — erst NACH Einwilligung aufrufen. */
export function loadGa(measurementId: string): void {
  if (gaLoaded || typeof window === "undefined" || !measurementId) return
  gaLoaded = true

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args)
  }
  window.gtag("js", new Date())
  // send_page_view: false — Pageviews feuern wir selbst bei App-Router-Navigation.
  window.gtag("config", measurementId, { send_page_view: false, anonymize_ip: true })

  const s = document.createElement("script")
  s.async = true
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
  document.head.appendChild(s)
}

/** Ist GA geladen (aktiviert + eingewilligt)? */
export function gaActive(): boolean {
  return gaLoaded && typeof window !== "undefined" && !!window.gtag
}

/** Ereignis senden — no-op ohne Einwilligung/Aktivierung. */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (!gaActive()) return
  try {
    window.gtag!("event", name, params ?? {})
  } catch {
    /* Tracking darf nie die Seite stören */
  }
}

export function trackPageView(path: string): void {
  trackEvent("page_view", { page_path: path, page_location: window.location.href, page_title: document.title })
}

/**
 * GA4-E-Commerce „purchase" für eine bezahlte Buchung — dedupliziert pro
 * Buchungsnummer (localStorage), damit Reloads der Erfolgsseite nicht
 * mehrfach zählen.
 */
export function trackPurchaseOnce(input: {
  transactionId: string
  valueCents: number
  items?: { item_name: string; price?: number; quantity?: number }[]
  coupon?: string
}): void {
  if (!gaActive()) return
  const key = `mk-ga-purchase-${input.transactionId}`
  try {
    if (localStorage.getItem(key)) return
    localStorage.setItem(key, new Date().toISOString())
  } catch {
    /* ohne localStorage lieber einmal zu viel als gar nicht */
  }
  trackEvent("purchase", {
    transaction_id: input.transactionId,
    value: Math.round(input.valueCents) / 100,
    currency: "EUR",
    ...(input.coupon ? { coupon: input.coupon } : {}),
    ...(input.items?.length ? { items: input.items } : {}),
  })
}
